import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import Box from "@mui/material/Box";

import { CORE_FIELD_KINDS, enumValueFor } from "@storyboard/dispatch-view/fieldKinds";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import { driverActionGuidance } from "@/hooks/dispatch/driverActions";
import type { DriverRow } from "@/types/driver";
import { zinc } from "@/ui/tokens";
import { DISPATCH_COLOR_VALUES } from "../dispatchColors";
import { ContextCaption, ContextText } from "./ContextBlock";

/**
 * Every action the driver owes, most pressing first: what is wrong and why it matters, then the words a dispatcher can
 * pass on to fix it. Renders nothing when the driver owes none, so the Storyboard status section opens on the callout
 * only when there is something to chase.
 */
export default function DriverActionsCallout({ driver }: { driver: DriverRow }) {
  const actions = driver.driverActions ?? [];
  if (actions.length === 0) return null;
  return (
    <Box
      data-sb={TestIds.dispatch.contextDriverActions}
      sx={{ display: "flex", flexDirection: "column", gap: 1, p: 1.5, borderBottom: `1px solid ${zinc[800]}` }}
    >
      {actions.map((action) => {
        const guidance = driverActionGuidance(action, driver);
        return (
          <Box
            key={action}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              p: 1.5,
              borderRadius: 1,
              border: `1px solid color-mix(in oklch, ${DISPATCH_COLOR_VALUES.orange} 45%, transparent)`,
              bgcolor: `color-mix(in oklch, ${DISPATCH_COLOR_VALUES.orange} 8%, transparent)`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: DISPATCH_COLOR_VALUES.orange }}>
              <WarningAmberOutlinedIcon aria-hidden sx={{ fontSize: 18 }} />
              <ContextText variant="value" component="span" sx={{ color: "inherit" }}>
                {enumValueFor(CORE_FIELD_KINDS.driver_action, action)?.label ?? action}
              </ContextText>
            </Box>
            <ContextText variant="subtext" sx={{ color: zinc[300] }}>
              {guidance.summary}
            </ContextText>
            <Box>
              <ContextCaption>Tell the driver</ContextCaption>
              <ContextText variant="value">{guidance.instruction}</ContextText>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
